import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  CircularProgress,
  Flex,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiGetAuth } from "../../../Backend/api";
import { useBookingBootstrap } from "../../Logic/useBookingBootstrap";
import { parseGuestAppConfig } from "../../../Helper/guestAppConfig";
import { ServiceDrawer } from "../Drawer/ServiceDrawer";

const buildNavPayload = (bookingDetails, manager) => {
  const ownerId = bookingDetails.room?.owner
    ? bookingDetails.room.owner.split("::")[0]
    : null;

  const room = {
    ...bookingDetails.room,
    owner: {
      id: ownerId,
      timezone: bookingDetails.owner?.timezone || "America/Los_Angeles",
    },
  };

  const booking = {
    ...bookingDetails.booking,
    securityStepsComplete: bookingDetails.securityStepsComplete === true,
    guestApp: parseGuestAppConfig(bookingDetails),
  };

  return {
    room,
    booking,
    manager,
    accessTimes: bookingDetails.accessTimes || null,
    storedStripeId: manager?.stripeId,
  };
};

export const UpsellPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bID = searchParams.get("bID");
  const upsellID = searchParams.get("upsellID");

  const {
    manager,
    loading,
    error,
    currentUser,
    bookingDetails,
    isLinkExpired,
  } = useBookingBootstrap(bID);

  const [upsell, setUpsell] = useState(null);
  const [upsellLoading, setUpsellLoading] = useState(true);
  const [upsellError, setUpsellError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navPayload = useMemo(() => {
    if (!bookingDetails || !manager?.name) return null;
    return buildNavPayload(bookingDetails, manager);
  }, [bookingDetails, manager]);

  const stepsDone = bookingDetails?.securityStepsComplete === true;

  // If security steps are incomplete, send them through onboarding first.
  // After they finish, DoneDisplay / main-v2 can still land them on stay.
  useEffect(() => {
    if (loading || error || isLinkExpired) return;
    if (!bookingDetails || currentUser.status !== "succeeded") return;
    if (!navPayload) return;
    if (stepsDone) return;

    navigate("/onboarding", {
      replace: true,
      state: {
        ...navPayload,
        upsellID,
      },
    });
  }, [
    loading,
    error,
    isLinkExpired,
    bookingDetails,
    currentUser.status,
    navPayload,
    stepsDone,
    navigate,
    upsellID,
  ]);

  // Load the target upsell once booking/auth is ready and security is done.
  useEffect(() => {
    if (!stepsDone || !navPayload?.room?.id || !upsellID) {
      if (!upsellID) {
        setUpsellError("Missing upsell link.");
        setUpsellLoading(false);
      }
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setUpsellLoading(true);
        setUpsellError(null);

        const res = await apiGetAuth(`/upsells/by-room/${navPayload.room.id}`, {
          limit: 100,
        });
        if (cancelled) return;

        const upsells = res?.data?.upsells || [];
        const target = upsells.find((u) => u.id === upsellID);

        if (!target) {
          setUpsellError("This offer is no longer available.");
          setUpsellLoading(false);
          return;
        }

        setUpsell(target);
        setDrawerOpen(true);
        setUpsellLoading(false);
      } catch (err) {
        console.error("Failed to load deep-link upsell:", err);
        if (!cancelled) {
          setUpsellError("Couldn't load this offer. Please try again.");
          setUpsellLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stepsDone, navPayload?.room?.id, upsellID]);

  const goToMain = () => {
    if (!navPayload) {
      navigate(`/go?bID=${encodeURIComponent(bID || "")}`, { replace: true });
      return;
    }
    navigate("/main-v2", {
      replace: true,
      state: {
        room: navPayload.room,
        booking: navPayload.booking,
        manager: navPayload.manager,
        accessTimes: navPayload.accessTimes,
      },
    });
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    goToMain();
  };

  const handlePurchaseComplete = () => {
    setDrawerOpen(false);
    goToMain();
  };

  if (!bID || !upsellID) {
    return (
      <Center minH="100vh" bg="gray.900" color="white" px={6}>
        <VStack spacing={3}>
          <Text fontWeight={700}>Invalid upsell link</Text>
          <Text fontSize="14px" color="whiteAlpha.700" textAlign="center">
            This link is missing booking or offer details.
          </Text>
        </VStack>
      </Center>
    );
  }

  if (isLinkExpired) {
    return (
      <Center minH="100vh" bg="gray.900" color="white" px={6}>
        <VStack spacing={3}>
          <Text fontWeight={700}>Link expired</Text>
          <Text fontSize="14px" color="whiteAlpha.700" textAlign="center">
            This booking link is no longer active. Please contact your host.
          </Text>
        </VStack>
      </Center>
    );
  }

  if (loading || currentUser.status !== "succeeded" || !bookingDetails) {
    return (
      <Center minH="100vh" bg="gray.900" color="white">
        <VStack spacing={4}>
          <CircularProgress
            isIndeterminate
            color="#817EFF"
            trackColor="transparent"
            size="40px"
          />
          <Text fontSize="14px" color="whiteAlpha.700">
            Loading your offer…
          </Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="100vh" bg="gray.900" color="white" px={6}>
        <VStack spacing={4}>
          <Text fontWeight={700}>Couldn't load booking</Text>
          <Text fontSize="14px" color="whiteAlpha.700" textAlign="center">
            {error}
          </Text>
          <Button
            bg="#817EFF"
            color="white"
            borderRadius="12px"
            onClick={() =>
              navigate(`/go?bID=${encodeURIComponent(bID)}`, { replace: true })
            }
          >
            Open stay instead
          </Button>
        </VStack>
      </Center>
    );
  }

  // Security incomplete — redirect effect above handles navigation.
  if (!stepsDone) {
    return (
      <Center minH="100vh" bg="gray.900" color="white">
        <VStack spacing={4}>
          <CircularProgress
            isIndeterminate
            color="#817EFF"
            trackColor="transparent"
            size="40px"
          />
          <Text fontSize="14px" color="whiteAlpha.700">
            Finishing required steps first…
          </Text>
        </VStack>
      </Center>
    );
  }

  if (upsellLoading) {
    return (
      <Center minH="100vh" bg="gray.900" color="white">
        <VStack spacing={4}>
          <CircularProgress
            isIndeterminate
            color="#817EFF"
            trackColor="transparent"
            size="40px"
          />
          <Text fontSize="14px" color="whiteAlpha.700">
            Opening offer…
          </Text>
        </VStack>
      </Center>
    );
  }

  if (upsellError || !upsell) {
    return (
      <Center minH="100vh" bg="gray.900" color="white" px={6}>
        <VStack spacing={4}>
          <Text fontWeight={700}>Offer unavailable</Text>
          <Text fontSize="14px" color="whiteAlpha.700" textAlign="center">
            {upsellError || "This offer could not be found."}
          </Text>
          <Button
            bg="#817EFF"
            color="white"
            borderRadius="12px"
            onClick={goToMain}
          >
            Continue to your stay
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.900" color="white">
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="100vh"
        px={6}
        gap={4}
        maxW="480px"
        mx="auto"
      >
        <Text fontSize={{ base: "18px", md: "22px" }} fontWeight={700} textAlign="center">
          {upsell.name}
        </Text>
        <Text fontSize="14px" color="whiteAlpha.700" textAlign="center">
          Complete your purchase below. When you're done you'll return to your stay.
        </Text>
        {!drawerOpen && (
          <Button
            bg="#817EFF"
            color="white"
            borderRadius="12px"
            onClick={() => setDrawerOpen(true)}
          >
            View offer
          </Button>
        )}
        <Button variant="ghost" color="whiteAlpha.800" onClick={goToMain}>
          Skip to stay
        </Button>
      </Flex>

      <ServiceDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        service={upsell}
        booking={navPayload.booking}
        // ServiceDrawer's prop is `onComplete` — this was previously passed
        // as `onPurchaseComplete` and silently ignored, so deep-link
        // purchases fell back to a plain close instead of the success flow.
        onComplete={handlePurchaseComplete}
      />
    </Box>
  );
};

export default UpsellPage;
